'use client';

import Image from 'next/image';
import type {
  ComponentPropsWithoutRef,
  HTMLAttributes,
  ReactNode,
} from 'react';
import { Container } from '../../components/container';
import { CustomLink } from '../../components/custom-link';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
} from '../../components/ui/navigation-menu';
import { cn } from '../../lib/utils';
import { GridItem } from './grid-item';

function DesktopMenuRoot({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLElement>) {
  return (
    <div className={cn('hidden lg:block', className)} {...props}>
      {children}
    </div>
  );
}

function DesktopMenuNav({
  children,
  className,
  viewportClassName,
  ...props
}: {
  children: ReactNode;
  viewportClassName?: string;
} & Omit<ComponentPropsWithoutRef<typeof NavigationMenu>, 'children'>) {
  return (
    <NavigationMenu
      className={cn('relative z-50 h-full', className)}
      {...props}
    >
      <NavigationMenuList className="flex h-[var(--header-height)] items-center">
        {children}
      </NavigationMenuList>

      <NavigationMenuViewport
        className={cn(
          'relative z-50 w-full bg-white shadow-md',
          viewportClassName
        )}
      />
    </NavigationMenu>
  );
}

function DesktopMenuLink({
  children,
  className,
  href,
  openInNewTab,
  ...props
}: {
  children: ReactNode;
  href: string;
  openInNewTab?: boolean;
} & HTMLAttributes<HTMLElement>) {
  return (
    <NavigationMenuItem
      className={cn('relative flex cursor-pointer items-center', className)}
      {...props}
    >
      <NavigationMenuLink
        className={cn(
          navigationMenuTriggerStyle(),
          'relative flex items-center after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0.5 after:bg-transparent'
        )}
        href={href}
        render={
          <CustomLink href={href} target={openInNewTab ? '_blank' : undefined}>
            {children}
          </CustomLink>
        }
      />
    </NavigationMenuItem>
  );
}

function DesktopMenuTrigger({
  children,
  title,
  className,
  href,
  ...props
}: {
  children: ReactNode;
  title: string;
  href: string;
} & HTMLAttributes<HTMLElement>) {
  return (
    <NavigationMenuItem {...props}>
      <CustomLink href={href}>
        <NavigationMenuTrigger
          className={cn(
            'relative after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0.5 after:bg-transparent',
            className
          )}
        >
          {title}
        </NavigationMenuTrigger>
      </CustomLink>
      <NavigationMenuContent className="max-h-[calc(100vh-var(--header-height))] w-full overflow-y-auto will-change-[transform,filter] data-[ending-style]:data-[activation-direction=left]:animate-exit-to-left data-[ending-style]:data-[activation-direction=right]:animate-exit-to-right data-[starting-style]:data-[activation-direction=left]:animate-enter-from-right data-[starting-style]:data-[activation-direction=right]:animate-enter-from-left">
        {children}
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}

function DesktopMegaMenuContainer({
  children,
  className,
  columns = 4,
  gap = 4,
  ...props
}: {
  children: ReactNode;
  columns?: number;
  gap?: number;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <Container>
      <div
        className={cn('grid w-full bg-white py-6', className)}
        style={{
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: `${gap * 4}px`,
        }}
        {...props}
      >
        {children}
      </div>
    </Container>
  );
}

function DesktopMegaMenuColumn({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-col gap-4', className)} {...props}>
      {children}
    </div>
  );
}

function DesktopMegaMenuColumnItems({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-col gap-2', className)} {...props}>
      {children}
    </div>
  );
}

function DesktopMegaMenuHeading({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn('mb-2', className)} {...props}>
      {children}
    </h3>
  );
}

function DesktopMegaMenuLinks({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLUListElement>) {
  return (
    <ul
      className={cn('flex list-none flex-col gap-2 p-0', className)}
      {...props}
    >
      {children}
    </ul>
  );
}

function DesktopMegaMenuLink({
  children,
  className,
  href,
  image,
  ...props
}: {
  children: ReactNode;
  href: string;
  image?: string;
} & HTMLAttributes<HTMLLIElement>) {
  return (
    <li className={cn('list-none', className)} {...props}>
      <CustomLink
        href={href}
        className={cn('group flex gap-3', {
          'justify-between': image,
        })}
      >
        <span>{children}</span>
        {image && (
          <div className="h-9 w-9 flex-shrink-0">
            <Image src={image} alt="" fill className="object-cover" />
          </div>
        )}
      </CustomLink>
    </li>
  );
}

function DesktopMegaMenuFeatured({
  children,
  className,
  ...props
}: {
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'container-bg-full grid max-h-[calc(100vh-var(--header-height))] w-full grid-cols-12 py-4',
        className
      )}
      style={{ gap: '36px', gridTemplateColumns: 'repeat(10, 1fr)' }}
      {...props}
    >
      {children}
    </div>
  );
}

function DesktopMegaMenuFeaturedContent({
  children,
  className,
  imageCount,
  ...props
}: {
  children: ReactNode;
  imageCount: number;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <section
      className={cn(
        'col-span-4',
        imageCount === 1 && 'col-start-3',
        imageCount === 2 && 'col-start-2',
        className
      )}
      {...props}
    >
      <div className="flex h-full flex-col items-start justify-center text-left">
        <div className="flex w-full flex-col items-start gap-4 text-left">
          {children}
        </div>
      </div>
    </section>
  );
}

function DesktopMegaMenuFeaturedImageWrapper({
  children,
  className,
  imageCount,
  ...props
}: {
  children: ReactNode;
  imageCount: number;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <section
      className={cn(
        imageCount === 1 && 'col-span-3',
        imageCount === 2 && 'col-span-4',
        className
      )}
      {...props}
    >
      <div className="flex h-full flex-col items-center justify-start text-center">
        <div className="flex w-full items-center gap-4 text-center">
          {children}
        </div>
      </div>
    </section>
  );
}

function DesktopMegaMenuImageWrapper({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('relative h-9 w-9 overflow-hidden', className)}
      {...props}
    >
      {children}
    </div>
  );
}

function DesktopMegaMenuColumnHeading({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h4 className={cn(className)} {...props}>
      {children}
    </h4>
  );
}

export function DesktopMenuShared({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLElement>) {
  return (
    <DesktopMenuRoot className={className} {...props}>
      {children}
    </DesktopMenuRoot>
  );
}

DesktopMenuShared.Root = DesktopMenuRoot;
DesktopMenuShared.Nav = DesktopMenuNav;
DesktopMenuShared.Link = DesktopMenuLink;
DesktopMenuShared.Trigger = DesktopMenuTrigger;
DesktopMenuShared.MegaMenu = DesktopMegaMenuContainer;
DesktopMenuShared.Column = DesktopMegaMenuColumn;
DesktopMenuShared.Heading = DesktopMegaMenuHeading;
DesktopMenuShared.Links = DesktopMegaMenuLinks;
DesktopMenuShared.LinkItem = DesktopMegaMenuLink;
DesktopMenuShared.Featured = DesktopMegaMenuFeatured;
DesktopMenuShared.FeaturedContent = DesktopMegaMenuFeaturedContent;
DesktopMenuShared.FeaturedImageWrapper = DesktopMegaMenuFeaturedImageWrapper;
DesktopMenuShared.ColumnItems = DesktopMegaMenuColumnItems;
DesktopMenuShared.GridItem = GridItem;
DesktopMenuShared.ImageWrapper = DesktopMegaMenuImageWrapper;
DesktopMenuShared.Column = DesktopMegaMenuColumn;
DesktopMenuShared.ColumnHeading = DesktopMegaMenuColumnHeading;
